import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { getCertificateTemplateByIdAsync } from '@share/api';
import type { ErrorResponse, ICertificateTemplate } from '@share/common-types';

export type ICertificateTemplateByIdError = Pick<ErrorResponse, 'title' | 'code'>;

export interface ICertificateTemplateByIdState {
  certificateTemplate: ICertificateTemplate | null;
  loading: boolean;
  error: ICertificateTemplateByIdError | null;
}

const initialState: ICertificateTemplateByIdState = {
  certificateTemplate: null,
  loading: false,
  error: null,
};

function parseCertificateTemplateByIdError(err: unknown): ICertificateTemplateByIdError {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (data && typeof data === 'object' && 'title' in data && 'code' in data) {
      const { title, code } = data as { title?: unknown; code?: unknown };
      if (typeof title === 'string' && typeof code === 'string') {
        return { title, code };
      }
    }
  }
  if (err instanceof Error) {
    return { title: err.message, code: 'request_failed' };
  }
  return { title: 'Unknown error', code: 'unknown_error' };
}

export const fetchCertificateTemplateById = createAsyncThunk<
  ICertificateTemplate,
  { id: string; clubSiteId?: number },
  { rejectValue: ICertificateTemplateByIdError }
>('CertificateTemplateById/fetchById', async ({ id, clubSiteId }, { rejectWithValue }) => {
  try {
    const { data } = await getCertificateTemplateByIdAsync(id, clubSiteId);
    return data;
  } catch (err: unknown) {
    return rejectWithValue(parseCertificateTemplateByIdError(err));
  }
});

const CertificateTemplateByIdSlice = createSlice({
  name: 'CertificateTemplateById',
  initialState,
  reducers: {
    clearCertificateTemplate: (state: ICertificateTemplateByIdState) => {
      state.certificateTemplate = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCertificateTemplateById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCertificateTemplateById.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.certificateTemplate = payload;
        state.error = null;
      })
      .addCase(fetchCertificateTemplateById.rejected, (state, { payload }) => {
        state.loading = false;
        state.certificateTemplate = null;
        state.error = payload ?? { title: 'Unknown error', code: 'unknown_error' };
      });
  },
});

export const CertificateTemplateByIdActions = CertificateTemplateByIdSlice.actions;

export const CertificateTemplateByIdReducer = CertificateTemplateByIdSlice.reducer;
